import type { FC } from "react";
import { useState } from "react";

import classNames from "classnames";

import { ChevronDownIcon } from "@jti/brand-icons/chevron-down";
import { UiMinusIcon } from "@jti/brand-icons/ui-minus";
import { UiPlusIcon } from "@jti/brand-icons/ui-plus";
import { LinkBehaviour, LinkPrefetchContext } from "@jti/core-components/components/LinkBehaviour";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { useHeaderContext } from "../../context";
import { PointsDisplay } from "../PointsDisplay";

import styles from "./Navigation.module.scss";

export type NavLinks = Array<NavLink>;
export type NavLink = {
  title: string;
  url: string;
  secondaryNavigation?: {
    id: string;
  };
  isActive: boolean;
  isLoyalty?: boolean;
  loyaltyPointsCurrency?: string;
};

export type NavigationProps = {
  navLinks: NavLinks;
  renderSubmenu?: (id: string, isDarkMode?: boolean) => JSX.Element;
  storeLocatorLink?: string;
};

export const Navigation: FC<NavigationProps> = ({ navLinks, renderSubmenu, storeLocatorLink }) => {
  const [openedIndex, setOpenedIndex] = useState<number | null>(null);
  // Tracks which submenus have been expanded (hovered on desktop, opened on mobile) at least once.
  // Until a submenu is expanded its links are rendered with prefetch disabled, so App Router
  // prefetch requests are not triggered for navigation the user never interacts with.
  const [expandedIndexes, setExpandedIndexes] = useState<Set<number>>(() => new Set());
  const { hoveredIndex, setHoveredIndex } = useHeaderContext();

  const markExpanded = (index: number) => {
    setExpandedIndexes((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  };

  const handleClick = (index: number) => {
    setOpenedIndex((prevIndex) => {
      const nextIndex = prevIndex === index ? null : index;
      if (nextIndex !== null) {
        markExpanded(nextIndex);
      }
      return nextIndex;
    });
  };

  const handleMouseEnter = (index: number) => {
    setHoveredIndex(index);
    markExpanded(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  const dispatchGTMEvent = (title: string, url: string) => {
    dispatchTrackingEvent(TrackingEvents.TrackingMenuNavigationClick, { menu_link: title });
    if (storeLocatorLink && url.includes(storeLocatorLink)) {
      dispatchTrackingEvent(TrackingEvents.TrackingStoreLocatorClick);
    }
  };

  return (
    <nav className={styles.container}>
      <ul className={styles.list}>
        {navLinks.map(({ title, url, secondaryNavigation, isActive, isLoyalty, loyaltyPointsCurrency }, index) => {
          const hasSubMenu = Boolean(secondaryNavigation);

          return (
            <li
              key={`${title}-${url}`}
              className={classNames({
                [styles.listItemWrapperOpened]: openedIndex === index,
              })}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            >
              <div className={styles.listItem}>
                <LinkBehaviour
                  href={url}
                  className={classNames(styles.link, {
                    [styles.linkWithSecondaryNavigation]: secondaryNavigation,
                    [styles.active]: isActive,
                  })}
                  title={title}
                  onClick={() => dispatchGTMEvent(title, url)}
                >
                  {title}
                  {isLoyalty && <PointsDisplay loyaltyCurrency={loyaltyPointsCurrency ?? ""} />}
                  {hasSubMenu && (
                    <ChevronDownIcon
                      className={classNames(styles.iconSize, styles.chevron, {
                        [styles.isHovered]: hoveredIndex === index,
                      })}
                    />
                  )}
                </LinkBehaviour>
                {hasSubMenu && (
                  <button
                    type="button"
                    className={classNames(styles.buttonToggler, {
                      [styles.buttonTogglerOpened]: openedIndex === index,
                    })}
                    onClick={() => handleClick(index)}
                  >
                    {openedIndex === index ? (
                      <UiMinusIcon className={styles.iconSize} />
                    ) : (
                      <UiPlusIcon className={styles.iconSize} />
                    )}
                  </button>
                )}
              </div>
              {hasSubMenu && (
                <div
                  className={classNames(styles.submenu, {
                    [styles.isHovered]: hoveredIndex === index,
                    [styles.submenuOpened]: openedIndex === index,
                  })}
                >
                  <LinkPrefetchContext.Provider value={expandedIndexes.has(index) ? undefined : false}>
                    {renderSubmenu?.(secondaryNavigation?.id ?? "")}
                  </LinkPrefetchContext.Provider>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
